import { Badge } from "~/components/ui/badge";
import { getDealImageUrl } from "~/lib/image";
import { formatPriceCentsForUser } from "~/lib/utils";
import { useUserStore } from "~/lib/stores/user";
import type { DealClientListData } from "~/types/deal";

interface DealCardProps {
  deal: DealClientListData;
  badgeText?: string;
  onClick?: () => void;
}

export function DealCard({ deal, badgeText, onClick }: DealCardProps) {
  const { user } = useUserStore();

  return (
    <div onClick={onClick} className="cursor-pointer">
      <div className="relative">
        {/* Product Image */}
        <div className="relative aspect-square">
          <div className="relative aspect-square overflow-hidden rounded-2xl">
            {deal.image ? (
              <img
                src={getDealImageUrl(deal._productId, deal.image, "full", "2x")}
                alt={deal.productName}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gray-100">
                <div className="text-sm text-gray-400">No Image</div>
              </div>
            )}

            {/* Border for normal deals */}
            <div className="pointer-events-none absolute inset-0 rounded-2xl border border-neutral-200" />

            {/* Badge */}
            {badgeText && (
              <div className="absolute top-2 left-2">
                <Badge variant="yellow">{badgeText}</Badge>
              </div>
            )}
          </div>
        </div>

        {/* Product Info */}
        <div className="mt-2.5">
          {/* Price */}
          <div className="mb-1 flex items-start gap-1">
            <span className="text-[16px] leading-[24px] font-semibold text-neutral-900">
              {formatPriceCentsForUser(deal.price || 0, user)}
            </span>
            {deal.strikethroughPrice && (
              <span className="text-[16px] leading-[24px] font-medium text-neutral-500 line-through">
                {formatPriceCentsForUser(deal.strikethroughPrice, user)}
              </span>
            )}
          </div>

          {/* Product Name */}
          <h3 className="mb-1 line-clamp-1 text-[14px] leading-[24px] font-medium text-neutral-700">
            {deal.productName}
          </h3>

          {/* Rating */}
          <div className="flex items-center gap-0.5">
            <svg className="h-3 w-3 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 12 11">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M6 1L7.545 4.13L11 4.635L8.5 7.07L9.09 10.51L6 8.885L2.91 10.51L3.5 7.07L1 4.635L4.455 4.13L6 1Z"
              />
            </svg>
            <span className="text-[12px] leading-[20px] font-medium text-neutral-600">
              {deal.rating ? deal.rating.toFixed(1) : "5.0"}(
              {deal.ratingCount && deal.ratingCount > 1000
                ? `${(deal.ratingCount / 1000).toFixed(0)}k`
                : deal.ratingCount || 0}
              )
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
